/**
 * @fileoverview Google Analytics event tracking via Firebase Analytics.
 * @description Thin wrapper over firebase/analytics so call sites never touch the Firebase SDK or
 * check configuration/support themselves. firebase/analytics is loaded via a dynamic import,
 * rather than a static one, so its code never enters the main bundle for the common case (no
 * Firebase project configured) — it only loads once a call site actually needs it. Every export
 * resolves the Analytics instance lazily — only after confirming a Firebase app exists and the
 * current browser can actually run Analytics — and silently no-ops otherwise, so tracking calls
 * are safe to sprinkle through application code unconditionally.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { getFirebaseApp } from '@/config/firebase'

import type { Analytics } from 'firebase/analytics'

let analyticsPromise: Promise<Analytics | null> | null = null

/**
 * @description Resolves the app's single Analytics instance, creating it on first call and
 * caching the resulting promise so firebase/analytics is fetched and support-checked only once.
 * @returns {Promise<Analytics | null>} The Analytics instance, or null when Firebase isn't
 * configured or the current browser doesn't support Analytics.
 */
function resolveAnalytics(): Promise<Analytics | null> {
  const app = getFirebaseApp()

  if (!app) {
    return Promise.resolve(null)
  }

  analyticsPromise ??= import('firebase/analytics')
    .then(async ({ isSupported, getAnalytics }) => {
      const supported = await isSupported()
      return supported ? getAnalytics(app) : null
    })
    .catch(() => null)

  return analyticsPromise
}

/**
 * @description Logs an analytics event. A no-op when Firebase Analytics isn't active.
 * @param {string} eventName - Name of the event (e.g. "login", "page_view").
 * @param {Record<string, unknown>} [eventParams] - Additional structured data for the event.
 */
export async function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>,
): Promise<void> {
  const analytics = await resolveAnalytics()

  if (!analytics) {
    return
  }

  const { logEvent } = await import('firebase/analytics')
  logEvent(analytics, eventName, eventParams)
}
